/**
 * VISTOERA_PLATFORM_ADMIN_MARKETING_ANALYTICS_HOME_AND_TRACKING_FOUNDATION_V1
 * — Platform Admin's new default home (task spec §4/§9/§57).
 *
 * One GET (`platformGetMarketingAnalyticsOverview`) per Date Range change
 * (task spec §42/§43/§76) — no polling (task spec §75), no per-KPI
 * request. Every number rendered here is either a real backend-
 * aggregated value or an explicit empty/unavailable state (task spec
 * §9/§60/§61) — never a fabricated placeholder like the reference UI's
 * own example numbers.
 */
import {
  BarChartOutlined,
  CrownOutlined,
  DollarOutlined,
  ReloadOutlined,
  RocketOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Table } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { platformGetMarketingAnalyticsOverview } from '../api'
import type {
  MarketingAnalyticsCampaign,
  MarketingAnalyticsCountry,
  MarketingAnalyticsKpi,
  MarketingAnalyticsLandingPage,
  MarketingAnalyticsOverview,
  MarketingAnalyticsRecentSignup,
} from '../types'
import { SectionCard, SummaryCard } from './adminShared'
import MarketingBarList from './MarketingBarList'
import MarketingFunnelChart from './MarketingFunnelChart'
import MarketingTrendChart from './MarketingTrendChart'
import { cardIconBg, platformColors } from './theme'

type RangeKey = '7d' | '30d' | '90d'
const RANGE_OPTIONS: RangeKey[] = ['7d', '30d', '90d']
const DEFAULT_RANGE: RangeKey = '30d'

function formatKpiValue(kpi: MarketingAnalyticsKpi, notAvailableLabel: string): string {
  if (!kpi.available || kpi.value === null) return notAvailableLabel
  return kpi.value.toLocaleString()
}

function formatDeltaPct(kpi: MarketingAnalyticsKpi, dash: string): string {
  if (!kpi.available || kpi.delta_pct === null) return dash
  const sign = kpi.delta_pct > 0 ? '+' : ''
  return `${sign}${kpi.delta_pct}%`
}

function formatShortDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return `${d.getMonth() + 1}/${d.getDate()}`
}

export default function MarketingAnalyticsPage() {
  const { t, i18n } = useTranslation(['marketingAnalytics'])
  const [range, setRange] = useState<RangeKey>(DEFAULT_RANGE)
  const [data, setData] = useState<MarketingAnalyticsOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback((r: RangeKey, opts?: { silent?: boolean }) => {
    if (!opts?.silent) setRefreshing(true)
    platformGetMarketingAnalyticsOverview(r)
      .then((res) => {
        setData(res.data)
        setError(null)
      })
      .catch((err) => {
        setError(err.message || 'failed')
      })
      .finally(() => {
        setLoading(false)
        setRefreshing(false)
      })
  }, [])

  useEffect(() => {
    setLoading(true)
    load(range)
  }, [range, load])

  const showSkeleton = loading && !data
  const notAvailableLabel = t('kpi.notAvailableYet')
  const dashLabel = t('kpi.noPreviousData')

  const mediumLabel = (medium: string): string => {
    const key = `trafficSources.medium.${medium}`
    const translated = t(key)
    return translated === key ? medium : translated
  }

  const deviceLabel = (deviceType: string): string => {
    const key = `deviceType.${deviceType}`
    const translated = t(key)
    return translated === key ? deviceType : translated
  }

  const funnel = data?.funnel
  const funnelStages = funnel
    ? [
        { key: 'visitors', label: t('funnel.visitors'), value: funnel.visitors, rateLabel: null, available: true },
        {
          key: 'signups',
          label: t('funnel.signups'),
          value: funnel.signups,
          rateLabel: funnel.signup_rate !== null ? `${t('funnel.signupRate')} ${funnel.signup_rate}%` : null,
          available: true,
        },
        {
          key: 'activated',
          label: t('funnel.activated'),
          value: funnel.activated,
          rateLabel: funnel.activation_rate !== null ? `${t('funnel.activationRate')} ${funnel.activation_rate}%` : null,
          available: true,
        },
        {
          key: 'paid',
          label: t('funnel.paidUsers'),
          value: funnel.paid,
          rateLabel: funnel.paid_rate !== null ? `${t('funnel.paidRate')} ${funnel.paid_rate}%` : null,
          available: funnel.paid_available,
        },
      ]
    : []

  const trafficSourceRows = (data?.traffic_sources ?? []).map((s) => ({
    key: `${s.source}/${s.medium}`,
    label: `${s.source === 'direct' ? t('trafficSources.sourceDirect') : s.source} · ${mediumLabel(s.medium)}`,
    value: s.visitors,
  }))

  const deviceRows = (data?.device_types ?? []).map((d) => ({
    key: d.device_type,
    label: deviceLabel(d.device_type),
    value: d.sessions,
  }))

  const newVsReturning = data?.new_vs_returning
  const newVsReturningRows = newVsReturning
    ? [
        { key: 'new', label: t('newVsReturning.new'), value: newVsReturning.new_visitors, color: platformColors.primary },
        { key: 'returning', label: t('newVsReturning.returning'), value: newVsReturning.returning_visitors, color: platformColors.info },
      ]
    : []

  return (
    <div style={{ maxWidth: 1600 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 20, lineHeight: '26px', fontWeight: 600, color: platformColors.primaryText }}>{t('pageTitle')}</div>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: 11,
                fontWeight: 600,
                padding: '1px 8px',
                borderRadius: 4,
                background: platformColors.primarySoft,
                color: platformColors.primary,
              }}
            >
              {t('pageBetaBadge')}
            </span>
          </div>
          <div style={{ fontSize: 12, lineHeight: '16px', color: platformColors.secondaryText, marginTop: 2 }}>{t('pageSubtitle')}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'inline-flex', border: `1px solid ${platformColors.border}`, borderRadius: 6, overflow: 'hidden' }}>
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setRange(opt)}
                style={{
                  border: 'none',
                  padding: '5px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: range === opt ? platformColors.primary : platformColors.surface,
                  color: range === opt ? '#fff' : platformColors.secondaryText,
                }}
              >
                {t(`dateRange.${opt}`)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => load(range)}
            disabled={refreshing}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 28,
              padding: '0 10px',
              borderRadius: 6,
              border: `1px solid ${platformColors.border}`,
              background: platformColors.surface,
              color: platformColors.primaryText,
              fontSize: 12,
              cursor: refreshing ? 'default' : 'pointer',
              opacity: refreshing ? 0.6 : 1,
            }}
          >
            <ReloadOutlined spin={refreshing} />
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            background: platformColors.dangerBg,
            color: platformColors.danger,
            border: `1px solid ${platformColors.dangerBg}`,
            borderRadius: 8,
            padding: '7px 14px',
            marginBottom: 10,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      {/* Row 1 — 6 KPI cards (task spec §9/§57). */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <SummaryCard
          title={t('kpi.websiteVisitors')}
          loading={showSkeleton}
          value={data ? formatKpiValue(data.summary.website_visitors, notAvailableLabel) : '—'}
          hint={data ? `${formatDeltaPct(data.summary.website_visitors, dashLabel)} ${t('kpi.vsPreviousPeriod')}` : undefined}
          iconBg={cardIconBg.tenant}
          icon={<UserOutlined style={{ color: platformColors.primary }} />}
        />
        <SummaryCard
          title={t('kpi.sessions')}
          loading={showSkeleton}
          value={data ? formatKpiValue(data.summary.sessions, notAvailableLabel) : '—'}
          hint={data ? `${formatDeltaPct(data.summary.sessions, dashLabel)} ${t('kpi.vsPreviousPeriod')}` : undefined}
          iconBg={cardIconBg.site}
          icon={<BarChartOutlined style={{ color: platformColors.info }} />}
        />
        <SummaryCard
          title={t('kpi.signups')}
          loading={showSkeleton}
          value={data ? formatKpiValue(data.summary.signups, notAvailableLabel) : '—'}
          hint={data ? `${formatDeltaPct(data.summary.signups, dashLabel)} ${t('kpi.vsPreviousPeriod')}` : undefined}
          iconBg={cardIconBg.domain}
          icon={<TeamOutlined style={{ color: platformColors.success }} />}
        />
        <SummaryCard
          title={t('kpi.activatedUsers')}
          loading={showSkeleton}
          value={data ? formatKpiValue(data.summary.activated_users, notAvailableLabel) : '—'}
          hint={data ? `${formatDeltaPct(data.summary.activated_users, dashLabel)} ${t('kpi.vsPreviousPeriod')}` : undefined}
          iconBg={cardIconBg.worker}
          icon={<RocketOutlined style={{ color: '#7C3AED' }} />}
        />
        <SummaryCard
          title={t('kpi.paidUsers')}
          loading={showSkeleton}
          value={data ? formatKpiValue(data.summary.paid_users, notAvailableLabel) : '—'}
          hint={
            data
              ? data.summary.paid_users.available
                ? `${formatDeltaPct(data.summary.paid_users, dashLabel)} ${t('kpi.vsPreviousPeriod')}`
                : t('revenueNote.notAvailable')
              : undefined
          }
          technicalNote={data && !data.summary.paid_users.available ? t('revenueNote.notAvailable') : null}
          iconBg={cardIconBg.task}
          icon={<CrownOutlined style={{ color: platformColors.warning }} />}
        />
        <SummaryCard
          title={t('kpi.revenue')}
          loading={showSkeleton}
          value={data ? formatKpiValue(data.summary.revenue, notAvailableLabel) : '—'}
          hint={
            data
              ? data.summary.revenue.available
                ? `${formatDeltaPct(data.summary.revenue, dashLabel)} ${t('kpi.vsPreviousPeriod')}`
                : t('revenueNote.notAvailable')
              : undefined
          }
          technicalNote={data && !data.summary.revenue.available ? t('revenueNote.notAvailable') : null}
          iconBg={cardIconBg.storage}
          icon={<DollarOutlined style={{ color: platformColors.success }} />}
        />
      </div>

      {/* Row 2 — Traffic Trend + Conversion Funnel (task spec §12/§13/§57). */}
      <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: '2 1 480px', minWidth: 320 }}>
          <SectionCard title={t('trafficTrend.title')}>
            <div style={{ padding: 8 }}>
              <MarketingTrendChart
                points={data?.traffic_trend ?? []}
                visitorsLabel={t('trafficTrend.visitors')}
                sessionsLabel={t('trafficTrend.sessions')}
                emptyLabel={t('trafficTrend.empty')}
                formatDate={formatShortDate}
              />
            </div>
          </SectionCard>
        </div>
        <div style={{ flex: '1 1 360px', minWidth: 300 }}>
          <SectionCard title={t('funnel.title')}>
            <div style={{ padding: 12 }}>
              {funnel ? (
                <MarketingFunnelChart stages={funnelStages} notAvailableLabel={t('funnel.notAvailable')} />
              ) : (
                <div style={{ padding: '24px 0', textAlign: 'center', color: platformColors.mutedText, fontSize: 13 }}>
                  {t('funnel.notAvailable')}
                </div>
              )}
            </div>
          </SectionCard>
        </div>
      </div>

      {/* Row 3 — Traffic Sources + Top Campaigns + Geographic Distribution
          (task spec §16/§31/§33/§57). */}
      <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', minWidth: 280 }}>
          <SectionCard title={t('trafficSources.title')}>
            <div style={{ padding: 12 }}>
              <MarketingBarList rows={trafficSourceRows} emptyLabel={t('trafficSources.empty')} />
            </div>
          </SectionCard>
        </div>
        <div style={{ flex: '1 1 420px', minWidth: 320 }}>
          <SectionCard title={t('campaigns.title')}>
            <Table<MarketingAnalyticsCampaign>
              className="pf-data-table"
              rowKey={(row) => `${row.campaign}-${row.campaign_id}`}
              dataSource={data?.top_campaigns ?? []}
              loading={showSkeleton}
              pagination={false}
              size="small"
              scroll={{ x: true }}
              locale={{ emptyText: t('campaigns.empty') }}
              columns={[
                {
                  title: t('campaigns.columns.campaign'),
                  dataIndex: 'campaign',
                  align: 'left' as const,
                  render: (v: string | null) => v ?? '—',
                },
                { title: t('campaigns.columns.visitors'), dataIndex: 'visitors', align: 'right' as const },
                { title: t('campaigns.columns.signups'), dataIndex: 'signups', align: 'right' as const },
                { title: t('campaigns.columns.activated'), dataIndex: 'activated', align: 'right' as const },
                {
                  title: t('campaigns.columns.paid'),
                  key: 'paid',
                  align: 'right' as const,
                  render: () => t('campaigns.notAvailable'),
                },
                {
                  title: t('campaigns.columns.conversion'),
                  dataIndex: 'conversion_rate',
                  align: 'right' as const,
                  render: (v: number | null) => (v !== null ? `${v}%` : '—'),
                },
              ]}
            />
          </SectionCard>
        </div>
        <div style={{ flex: '1 1 320px', minWidth: 280 }}>
          <SectionCard title={t('geography.title')}>
            <Table<MarketingAnalyticsCountry>
              className="pf-data-table"
              rowKey={(row) => row.country_code ?? 'unknown'}
              dataSource={data?.geography ?? []}
              loading={showSkeleton}
              pagination={false}
              size="small"
              scroll={{ x: true }}
              locale={{ emptyText: t('geography.empty') }}
              columns={[
                {
                  title: t('geography.columns.country'),
                  dataIndex: 'country_code',
                  align: 'left' as const,
                  render: (code: string | null) => code ?? t('geography.unknown'),
                },
                { title: t('geography.columns.visitors'), dataIndex: 'visitors', align: 'right' as const },
              ]}
            />
          </SectionCard>
        </div>
      </div>

      {/* Row 4 — Device Type + New vs Returning + Top Landing Pages +
          Recent Signups (task spec §36/§38/§39/§40/§57). */}
      <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px', minWidth: 240 }}>
          <SectionCard title={t('deviceType.title')}>
            <div style={{ padding: 12 }}>
              <MarketingBarList rows={deviceRows} emptyLabel={t('deviceType.empty')} />
            </div>
          </SectionCard>
        </div>
        <div style={{ flex: '1 1 260px', minWidth: 240 }}>
          <SectionCard title={t('newVsReturning.title')}>
            <div style={{ padding: 12 }}>
              <MarketingBarList rows={newVsReturningRows} emptyLabel={t('newVsReturning.empty')} />
            </div>
          </SectionCard>
        </div>
        <div style={{ flex: '1 1 360px', minWidth: 300 }}>
          <SectionCard title={t('landingPages.title')}>
            <Table<MarketingAnalyticsLandingPage>
              className="pf-data-table"
              rowKey="landing_page"
              dataSource={data?.top_landing_pages ?? []}
              loading={showSkeleton}
              pagination={false}
              size="small"
              scroll={{ x: true }}
              locale={{ emptyText: t('landingPages.empty') }}
              columns={[
                { title: t('landingPages.columns.landingPage'), dataIndex: 'landing_page', align: 'left' as const },
                { title: t('landingPages.columns.visitors'), dataIndex: 'visitors', align: 'right' as const },
                { title: t('landingPages.columns.signups'), dataIndex: 'signups', align: 'right' as const },
                { title: t('landingPages.columns.activated'), dataIndex: 'activated', align: 'right' as const },
                {
                  title: t('landingPages.columns.conversion'),
                  dataIndex: 'conversion_rate',
                  align: 'right' as const,
                  render: (v: number | null) => (v !== null ? `${v}%` : '—'),
                },
              ]}
            />
          </SectionCard>
        </div>
        <div style={{ flex: '1 1 360px', minWidth: 300 }}>
          <SectionCard title={t('recentSignups.title')}>
            <Table<MarketingAnalyticsRecentSignup>
              className="pf-data-table"
              rowKey={(row) => `${row.masked_email}-${row.signup_time}`}
              dataSource={data?.recent_signups ?? []}
              loading={showSkeleton}
              pagination={false}
              size="small"
              scroll={{ x: true }}
              locale={{ emptyText: t('recentSignups.empty') }}
              columns={[
                { title: t('recentSignups.columns.user'), dataIndex: 'masked_email', align: 'left' as const },
                {
                  title: t('recentSignups.columns.source'),
                  dataIndex: 'source',
                  align: 'left' as const,
                  render: (v: string) => (v === 'direct' ? t('recentSignups.sourceDirect') : v),
                },
                {
                  title: t('recentSignups.columns.campaign'),
                  dataIndex: 'campaign',
                  align: 'left' as const,
                  render: (v: string | null) => v ?? '—',
                },
                {
                  title: t('recentSignups.columns.signupTime'),
                  dataIndex: 'signup_time',
                  align: 'left' as const,
                  render: (v: string | null) => {
                    if (!v) return '—'
                    const d = new Date(v)
                    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(i18n.language, { hour12: false })
                  },
                },
              ]}
            />
          </SectionCard>
        </div>
      </div>

      {data && (
        <div style={{ marginTop: 10, fontSize: 11, color: platformColors.mutedText }}>
          {t('environmentNote', { environment: data.environment })}
        </div>
      )}
    </div>
  )
}
